export enum ViewsTypesEnum {
  MY_JOBS = 'my-jobs',
  ALL_PUBLIC_JOBS = 'all-public-jobs',
}

export enum MyJobsTabsEnum {
  NEW = 'new',
  APPLIED = 'applied',
  SAVED = 'saved',
  AVAILABILITY = 'availability',
}

export enum JobIdSource {
  RECOMMENDATION = 'recommendation',
  ASSIGNMENT = 'assignment',
  SAVED = 'saved',
}

export const MY_JOBS_TAB_LABELS: Record<MyJobsTabsEnum, string> = {
  [MyJobsTabsEnum.NEW]: 'Invitations',
  [MyJobsTabsEnum.APPLIED]: 'Applied',
  [MyJobsTabsEnum.SAVED]: 'Saved',
  [MyJobsTabsEnum.AVAILABILITY]: 'Availability',
};

interface Company {
  id: number;
  name: string;
  profileImageUrl: string;
  isValidatedCompany: boolean;
  rating?: number;
  reviewsCount?: number;
  profileURLName?: string;
  premium?: boolean;
}

export type CompaniesSuggestion = { suggestions: Company[] };

export interface JobRecommendationJobDetail {
  id: number;
  title: { id: number; name: string } | string; // Backend inconsistency: object or string
  description: string;
  company?: Company;
  address: { googleAddress: string; latitude?: number; longitude?: number };
  payRate: number;
  payRatePeriod: string;
  // FIXME: Why use different date fields on each tab?
  // As the Job model states, dates works like so:
  // start, end -> field that store the full range between earliest and latest shift
  // jobStart, jobEnd -> fields that store the original (job) start/end dates which are added for the job post on creation
  start?: string; // APPLIED/SAVED tabs
  end?: string; // APPLIED/SAVED tabs
  jobStart?: string;
  jobEnd?: string;
  startDate?: string; // NEW tab
  endDate?: string; // NEW tab
  openPositions: number;
  nationalJob: boolean;
  ongoingJob: boolean;
  status?: string;
  eventName: string;
  brandRepresented: string;
  skills: string[];
  publicUrl: string | null;
  pictures?: string[];
}

export enum NotificationFrequency {
  EVERY_4_HOURS = 'EVERY_4_HOURS',
  DAILY = 'DAILY',
  PAUSED = 'PAUSED',
}

export enum NotificationChannels {
  EMAIL = 'EMAIL',
  PUSH_NOTIFICATION = 'PUSH_NOTIFICATION',
  WEB = 'WEB',
}

export enum JobRecommendationStatus {
  NEW = 'NEW',
  VIEWED = 'VIEWED',
  APPLIED = 'APPLIED',
  SAVED = 'SAVED',
  EXPIRED = 'EXPIRED',
  DECLINED = 'DECLINED',
}

export enum JobRecommendationType {
  BLAST_SHARE = 'BLAST_SHARE',
  COMPANY_SHARE = 'COMPANY_SHARE',
  DIRECT_OFFER = 'DIRECT_OFFER',
}

export interface JobPreferencePayload {
  id: number;
  titles: [
    {
      id: number;
    },
  ];
  companies: [
    {
      id: number;
    },
  ];
  minimumPayRate: number;
  receiveBy: NotificationChannels[];
  frequency: NotificationFrequency;
}

/**
 * Additional address a worker can configure in My Jobs to also receive job alerts around it.
 * Multiple preferred addresses are an In-Network (premium) only feature.
 */
export interface PreferredAddress {
  id?: number;
  address: string;
  addressLatitude: number;
  addressLongitude: number;
  maxDistanceMiles: number;
}

export interface JobRecommendationsPreferences {
  id: number;
  minPayRateHourly: number | null;
  minPayRateDaily: number | null;
  maxDistanceMiles: number;
  notificationFrequency: NotificationFrequency;
  notificationPausedUntil: string | null;
  preferredChannels: Array<NotificationChannels>;
  preferredTitles: { id: number; name: string }[];
  ignoredCompanies?: { id: number; name: string }[];
  preferredAddresses?: PreferredAddress[];
  allowSuggestedAppearance: boolean;
  setupCompletedAt: string | null;
  dateCreated: string;
  lastUpdated: string;
}

export interface JobRecommendationsPreferencesResponse
  extends JobRecommendationsPreferences {}

export interface JobRecommendationsPreferencesPayload
  extends JobRecommendationsPreferences {}

export type JobsConfigFormValues = {
  frequency: NotificationFrequency;
  receiveChannels: NotificationChannels[];
  jobTitles: string[]; // Job title IDs as strings (Mantine Checkbox.Group requirement)
  minHourlyEnabled: boolean;
  minHourlyRate: number | undefined;
  minDailyEnabled: boolean;
  minDailyRate: number | undefined;
  currentAddress: string; // Worker's profile address (read-only here, edited in Profile)
  distance: number; // Max distance for the profile address (maxDistanceMiles)
  preferredAddresses: PreferredAddress[];
  excludeCompany: string[]; // Company IDs as strings (Mantine MultiSelect requirement)
  allowSuggestedAppearance: boolean;
};

export interface JobRecommendation {
  id: number;
  workerId: number;
  jobId?: number;
  recommendationType: JobRecommendationType;
  recommendationScore: number;
  status: JobRecommendationStatus;
  sharedAt: string | null;
  viewedAt: string | null;
  appliedAt: string | null;
  savedAt: string | null;
  expiredAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface JobApplication {
  id: number;
  workerResponsesId: number | null;
  dateApplied: string; // ISO date string
  jobAssignmentId: number;
  applicationNote: string;
  origin: string;
}

/** JobAssignment status values from backend enum */
export enum JobAssignmentStatus {
  SHARED = 'SHARED',
  WORKER_APPLIED = 'WORKER_APPLIED',
  WORKER_INTERESTED = 'WORKER_INTERESTED',
  WORKER_NOT_INTERESTED = 'WORKER_NOT_INTERESTED',
  DECLINED_BY_COMPANY = 'DECLINED_BY_COMPANY',
  OFFERED = 'OFFERED',
  HIRED = 'HIRED',
  DECLINED_BY_WORKER = 'DECLINED_BY_WORKER',
  CANCELLED_BY_WORKER = 'CANCELLED_BY_WORKER',
  CANCELLED_BY_COMPANY = 'CANCELLED_BY_COMPANY',
  TIMESHEET_COMPLETED = 'TIMESHEET_COMPLETED',
  TIMESHEET_APPROVED = 'TIMESHEET_APPROVED',
}

/** Statuses that allow chat (worker is engaged with the job) */
export const CHAT_ENABLED_STATUSES: JobAssignmentStatus[] = [
  JobAssignmentStatus.WORKER_APPLIED,
  JobAssignmentStatus.WORKER_INTERESTED,
  JobAssignmentStatus.OFFERED,
  JobAssignmentStatus.HIRED,
  JobAssignmentStatus.TIMESHEET_COMPLETED,
  JobAssignmentStatus.TIMESHEET_APPROVED,
];

export interface JobAssignment {
  id: number;
  jobId: number;
  workerId: number;
  matchSkills: boolean;
  distance?: number; // Distance in miles from worker's location
  status?: JobAssignmentStatus; // Assignment status from backend
  hired?: boolean; // True when worker is hired for this job
}

export interface WorkerSavedJob {
  savedJobId: number;
  savedAt: string;
  jobId: number;
}

/** Backend response DTO (used by all tabs) */
export interface WorkerJobCommonResultDTO {
  jobRecommendation?: JobRecommendation;
  jobApplication?: JobApplication;
  jobAssignment?: JobAssignment;
  workerSavedJob?: WorkerSavedJob;
  job?: JobRecommendationJobDetail;
  company?: Company;
  distance?: number;
  // Backend-provided flags for button logic
  isActive?: boolean; // False for past/expired jobs
  canApply?: boolean; // True if worker can apply to this job
  canUnApply?: boolean; // True if worker can un-apply from this job
}

export interface WorkerJobCommonResultsResponse {
  pagination: {
    offset: number;
    max: number;
    count: number;
  };
  results: WorkerJobCommonResultDTO[];
}

export type JobRecommendationResponse = WorkerJobCommonResultsResponse;
export type AppliedJobsResponse = WorkerJobCommonResultsResponse;
export type SavedJobsResponse = WorkerJobCommonResultsResponse;

export interface SavedJobResponse {
  id: string;
  workerId: number;
  jobId: number;
  savedAt: string;
  notes: string;
  reminderDate: string;
}

/** Normalized job detail (title always string, company and dates required) */
export interface NormalizedJobDetail
  extends Omit<
    JobRecommendationJobDetail,
    'title' | 'company' | 'startDate' | 'endDate'
  > {
  title: string;
  company: Company;
  startDate: string;
  endDate: string;
}

/** Component-friendly job item (from WorkerJobCommonResultDTO) */
export interface TransformedJobItem {
  id: number; // recommendation/assignment/saved ID
  job: NormalizedJobDetail | null;
  workerId?: number;
  jobId?: number;
  savedAt?: string | null;
  appliedAt?: string | null;
  distance?: number | null; // Distance in miles from worker's location
  jobAssignmentId?: number | null; // For chat: available when job is shared (COMPANY_SHARE/BLAST_SHARE)
  jobAssignmentStatus?: JobAssignmentStatus | null; // For chat: status determines if chat is enabled
  // Backend-provided flags for button logic
  isActive?: boolean; // False for past/expired jobs
  canApply?: boolean; // True if worker can apply to this job
  canUnApply?: boolean; // True if worker can un-apply from this job
  hired?: boolean; // True when worker is hired for this job
}

/** Job question types */
export type JobQuestionType =
  | 'open-field'
  | 'multiple-choice'
  | 'attach-file'
  | 'video';

export interface JobQuestion {
  id: number;
  text: string;
  type: JobQuestionType; // API returns "type", not "questionType"
  choices?: string[]; // For multiple-choice questions (API returns "choices")
  required?: boolean;
}

export interface JobQuestionResponse {
  questionId: number;
  data: string;
}

/** Mutation action types for job list items */
export enum JobMutationAction {
  APPLY = 'apply',
  UNAPPLY = 'unapply',
  SAVE = 'save',
  UNSAVE = 'unsave',
  DISMISS = 'dismiss',
}

/** Unified mutation state passed to job list items */
export interface JobMutationState {
  action: JobMutationAction | null;
  targetId: number | null;
}

/**
 * Availability types and interfaces for My Jobs - Availability tab and related components/utilities
 * */

export enum AvailabilityType {
  NOT_AVAILABLE = 'NOT_AVAILABLE',
  ADDRESS_CHANGE = 'ADDRESS_CHANGE',
  WORK = 'WORK',
}

/** API response for a single worker availability entry */
export interface WorkerAvailabilityResponse {
  id: string;
  workerId: number;
  isAllDay: boolean;
  startDate: string;
  endDate: string;
  startTime: string;
  endTime: string;
  type: AvailabilityType;
}

/** Availability entry for worker unavailable dates (component-level) */
export type AvailabilityEntry = Readonly<{
  id: string;
  startDate: Date;
  endDate: Date;
  isAllDay: boolean;
  startTime?: string;
  endTime?: string;
}>;

/** Form data for creating/editing availability entries */
export type AvailabilityFormData = {
  dateRange: [Date | null, Date | null];
  isAllDay: boolean;
  startTime: string;
  endTime: string;
};
